/* eslint-disable max-statements */
import { MESSAGE_TIMEOUT } from '@ds/swymkit/config'
import type {
  RouteLocation,
  RouteLocationAsRelative,
  RouteLocationNormalized,
  RouteLocationResolved,
  Router,
} from 'vue-router'
import { message } from 'vuekit'

import { ROUTE_ADMIN } from '~/contexts/admin-tenant/router/routes'
import { ROUTE_HOME } from '~/contexts/home/router/routes'
import { ROUTE_ME } from '~/contexts/me/router/routes'
import { ROUTE_NETWORK } from '~/contexts/network/router/routes'
import { ROUTE_RITUAL } from '~/contexts/ritual/router/routes'

import {
  getCommentOrReplyUrl,
  getCommunityOrContentUrl,
  getCommunityPageUrl,
  getCommunityUrl,
  getDMUrl,
  getDetailViewUrl,
  getDmMessageUrl,
  getProfileUrl,
  getRitualsUrl,
  getThreadUrl,
  getUserProfileUrl,
} from '~/router/navigation'
import { router } from '~/router/router'
import type { getContentDataFromHashReturn } from '~/router/utils/url-mappings'
import {
  extractCommunityRouteData,
  extractProfileRouteData,
  extractRitualRoutedata,
  getContentDataFromHash,
} from '~/router/utils/url-mappings'

import type {
  CommunityType,
  NonMemberPrivateCommunityAccessError,
} from '~/entities/container/community/interfaces/community'
import { ECommunityAccess } from '~/entities/container/community/interfaces/community'
import { useCommunitiesStore } from '~/entities/container/community/stores/communities'
import { useUsersStore } from '~/entities/user/user/stores/users'

import type { FacetName } from '~/features-swym/control-center/interfaces/admin-tenant'

import { getSwymUrl } from '~/features-shared/config-manager/stores/swym-config'
import { fetchSwymContent } from '~/features-shared/content/api/contents'
import {
  openInNewTab,
  showTransientWidget,
  toBeOpenedInNewWindow,
  toBeOpenedInTransientWidget,
} from '~/features-shared/widget/utils/widget-link-navigation'

import { hasAccessTo } from '~/features-generic/config-manager/composables/page-manager'
import { APPNAME, CONTROL_CENTER_APPNAME, PROFILE_APPNAME } from '~/features-generic/libs/config/widget-app-name'
import type { ModalCommunitySignupRequestProps } from '~/features-generic/modals/interfaces/modals'
import { useTranslations } from '~/features-generic/translations/composables/translations'

import { SwymErrorCode } from '~/interfaces/services-dto'

import type { RoutesName } from '~/typings/vue-router'

const STATUS_403 = 403
const STATUS_404 = 404

/** Display "This content doesnt exist!" error message */
async function showContentUnavailableError() {
  const { $i18n, promise } = useTranslations('error')
  await promise
  message.create({
    text: $i18n('ThisContentDoesntExist'),
    timeout: MESSAGE_TIMEOUT,
  })
}

function isContentPage(route: RouteLocationNormalized) {
  return route.query && (route.query.content || route.query.media)
}

function contentChanged(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  return (to.query.content !== from.query.content)
    || (to.query.media !== from.query.media)
    || (to.query.edit !== from.query.edit)
}

/**
 * Check if error is non-member private community access error and open community signup request modal if true.
 * @param error Error instance to be checked.
 * @returns true if community signup request modal is opened.
 */
export function isPrivateCommunityAccessError(error: NonMemberPrivateCommunityAccessError) {
  const errorStatus = error.response?.status
  const communityData = error.data?.result

  if ((errorStatus === STATUS_403)
    && (communityData?.access === ECommunityAccess.Private)
    && !communityData.tou_to_sign) {
    // error not linked to tou
    // tou is handled by community nav guard, not here
    // community is private, so show modal for community signup request
    const modalProps: ModalCommunitySignupRequestProps = {
      community: communityData,
    }

    useEventBus('swym-app')
      .emit('modal-manager:open', {
        component: 'modal-community-signup-request',
        props: modalProps,
      })

    return true
  }

  return false
}

/**
 * Check if the route hash includes a X3DContentID
 * @param to Route to check
 */
const isX3DContentId = (to: RouteLocationNormalized) => /#content:X3DContentId/.exec(to.hash)

/**
 * Check if the route hash is from notification panel i.e check app key in hash string.
 * @param to Route to check
 */
const isDashBoardUrl = (to: RouteLocationNormalized) => to.hash.includes(`#app:${APPNAME}`) || to.hash.includes(`#app:${PROFILE_APPNAME}`) || to.hash.includes(`#app:${CONTROL_CENTER_APPNAME}`)

/**
 * Check content access
 * - Access right
 * - Force edit mode when the content is a draft
 * - Check wiki access
 * @param to route to check, contains the content uri
 * @param _from route to check, contains the content uri
 */
function checkContentAccess(to: RouteLocationNormalized, _from?: RouteLocationNormalized) {
  // Media
  const isMedia = to.query.media && (to.query.content === undefined)

  // Media in content
  const isMediaInContent = to.query.content && to.query.media

  // Fetch query.media if the content is a media or media in a content
  // Else fetch query.content
  const contentToFetch = (isMedia || isMediaInContent) ? to.query.media : to.query.content

  return fetchSwymContent(contentToFetch as string)
    .then((content) => {
      // Content is draft and don't have permission to edit
      if (!content?.published && to.query.edit !== 'true') {
        to.query.edit = 'true'
        return { redirection: true }
      }

      // don't have permission to edit OR ODE edit disabled/unavailable
      if (to.query.edit === 'true' && (!content?._acl?.update.access)) {
        const { $i18n } = useTranslations('error')
        // show warning if no rights to edit
        message.create({ text: `${$i18n('NoSufficientRightsEdit')}`, timeout: MESSAGE_TIMEOUT })
        // @ts-expect-error this is fine, it will force the remove of the query param
        to.query.edit = undefined
        return { redirection: true }
      }
    })
}

/**
 * Get content subjectURI from URL
 */
function getX3DContentId(hash = '') {
  const objectId = /"objectId":"([^"]*)"/.exec(hash)?.[1]
  const objectType = /"objectType":"([^"]*)"/.exec(hash)?.[1]
  return { objectId, objectType }
}
/**
 * Check if community is personal space
 */
async function isPersonalSpaceCommunity(communityUri: string) {
  const storeCommunities = useCommunitiesStore()
  try {
    const communityData = await storeCommunities.fetchCommunity(communityUri)
    if (communityData?.community_type === 'personalspace')
      return true
  }
  catch {
    return false
  }
}

/**
 * Get detail view url of content included in transiant URL
 * @param route includes the content info
 */
async function getX3DContentIdPath(route: RouteLocationNormalized): Promise<RouteLocation> {
  const data = getX3DContentId(decodeURIComponent(route.hash))
  let resultRoute: RouteLocationResolved = router.resolve({ name: ROUTE_HOME.HOME })
  let userHash = ''
  /**
   * ObjectType is a blank string in the case of potions lab URLs and thus when it is a blank string,
   * it is assumed that the URL is for a content.(Default case in below switch)
   */
  if (data.objectId) {
    switch (data.objectType) {
      case 'swym:PersonalSpace':
        resultRoute = getThreadUrl('personalspace')
        break
      case 'swym:Community':
      // Go To Community
        resultRoute = await isPersonalSpaceCommunity(data.objectId)
          ? router.resolve({ name: ROUTE_ME.PERSONAL_SPACE_HOME })
          : getCommunityUrl(data.objectId)
        break
      case 'pno:Person':
      // Go To profile
        userHash = data.objectId.split(':')[1] // Get Profile hash
        return getUserProfileUrl(userHash)
      case 'swym:DirectMessage':
      // GO To DM
        resultRoute = getDMUrl(data.objectId)
        break
      default: // Default case is for content
      // Go To Content
        resultRoute = getDetailViewUrl(
          { subject_uri: data.objectId as string },
          false,
          true,
        )
        break
    }
  }
  return resultRoute
}

/**
 * check is the page manager allows access to the page
 * @param to route to check
 */
function checkPageManagerAccess(to: RouteLocationNormalized) {
  // @ts-expect-error this error can be safely ignored
  const pageName = to.name?.replace('content_type', to.params.content_type || '')
  return hasAccessTo(pageName as RoutesName)
}

/**
 * Get v3 URL for a community from a v2 URL (hash)
 * @param hash v2 URL
 * @param communityType community or dm
 */
function handleCommunityDMRouting(hash: string, communityType: CommunityType) {
  // Extracted community route data
  const communityRouteData = extractCommunityRouteData(hash, communityType)
  // Case : It is a reply, comment or a answer url
  if (communityRouteData.reply || communityRouteData.comment || communityRouteData.answer)
    return getCommentOrReplyUrl(communityRouteData)
  // Case: It is a community page. for example: 'settings', 'members'
  if (communityRouteData.page)
    return getCommunityPageUrl(communityRouteData)

  // Case: It is a DM url pointing to a message in the conversation.
  if (communityRouteData.messageId)
    return getDmMessageUrl(communityRouteData)

  // Case: It is a just a community/DM url
  return getCommunityOrContentUrl(communityRouteData)
}

/**
 * Get route of a content from data extracted from v2 URL
 * @param contentData extracted data. Should contains contentId to work
 */
async function handleContentRoute(contentData: getContentDataFromHashReturn):
Promise<ReturnType<Router['resolve']> | undefined> {
  if (!contentData.contentId)
    return

  const content = (await fetchSwymContent(
    contentData.contentId,
    false,
    contentData.contentType,
  ))

  if (content) {
    const detailUrl = getDetailViewUrl(
      content,
      false,
      true,
    )
    return router.resolve({ ...detailUrl, name: ROUTE_HOME.HOME })
  }
}

/**
 * Resolve v2 URL to v3 URL
 * @param hash v2 URL
 * @returns throw error if route was not found
 */
/* eslint-disable-next-line complexity */
export async function checkV2HashURL(hash: string): Promise<ReturnType<Router['resolve']>> {
  /* Everything after '?' is a query string and has to be preserved in transformed v3 url.
     This is necessary in the case where solidworks content link generates a swym-v2 link which
     has a query parameter which must be preserved in the v3 url as well.
  */
  const [_hash, queryString] = hash.replace('#', '')
    .split('?')

  let route: ReturnType<Router['resolve']> | undefined
  if (_hash.includes('home')) {
    route = router.resolve({ name: ROUTE_HOME.HOME })
  }
  else if (_hash.includes('mybookmarks')) {
    route = router.resolve({ name: ROUTE_ME.BOOKMARKS })
  }
  else if (_hash.includes('mynetworkactivity')) {
    route = router.resolve({ name: ROUTE_NETWORK.ACTIVITY })
  }
  else if (_hash.includes('admin-tenant')) {
    const facet_name = _hash.split('/')[1] as FacetName
    route = facet_name
      ? router.resolve({ name: ROUTE_ADMIN.ADMIN_TENANT_FACET, params: { facet_name } })
      : router.resolve({ name: ROUTE_ADMIN.ADMIN_TENANT })
  }
  else if (_hash.includes('mycontributions')) {
    route = router.resolve({ name: ROUTE_ME.CONTRIBUTIONS_HOME })
  }
  else if (_hash.startsWith('community:') || _hash.startsWith('dm:')) {
    route = await handleCommunityDMRouting(_hash, _hash.startsWith('community:') ? 'community' : 'dm')
  }
  else if (_hash.includes('people:')) {
    const profile = extractProfileRouteData(_hash)
    // Navigate to profile lightbox for other user
    // Navigate to me-profile for self user
    route = await getProfileUrl(profile)
  }
  else if (_hash.includes('rituals/calendar')) {
    route = router.resolve({ name: ROUTE_RITUAL.SCHEDULE })
  }
  else if (_hash.includes('ritual')) {
    const ritual = extractRitualRoutedata(_hash)
    if (ritual)
      route = getRitualsUrl(ritual)
    else if (_hash.includes('rituals'))
      route = router.resolve({ name: ROUTE_RITUAL.HOME })
  }
  else {
    // Special Case for when URL has contentId without any communityId
    const contentData = getContentDataFromHash(_hash)
    if (contentData.contentId)
      route = await handleContentRoute(contentData)
  }

  if (!route)
    throw new Error('Invalid Route')

  // If V2 route has a query string, then it must pe preserved in the v3 route as well.
  const v2Query = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {}
  return router.resolve({
    ...route,
    query: {
      ...route.query,
      ...v2Query,
    },
  })
}

/**
 * Opens user profile in widget preview in 3DD(Transient widget).
 * @param to User profile route.
 */
function openProfileWidgetInTransientMode(to: RouteLocationNormalized) {
  if (to.query.profile) {
    const userStore = useUsersStore()
    // Fetch or get user
    userStore.getUser(router.getQuery(to, 'profile')!)
      .then((user) => {
        const appData = {
          login: user?.login,
          hash_key: user?.hash_key,
          url: getSwymUrl(),
        }
        // Open profile transient widget
        showTransientWidget({
          appKey: PROFILE_APPNAME,
          appTitle: '',
          appData,
        })
      })
  }
  else if (to.name === 'me-profile-index') {
    // Get current user
    const currentUser = useUsersStore().currentUser
    const appData = {
      login: currentUser?.login,
      hash_key: currentUser?.hash_key,
      url: getSwymUrl(),
    }
    // Open profile transient widget
    showTransientWidget({
      appKey: PROFILE_APPNAME,
      appTitle: '',
      appData,
    })
  }
}

/**
 * Setup router page access. It allows to prevent navigation to restricted routes.
 * - Resolve V2 URL to V3 URL
 * - Check PageManager access
 * - Check access on Content
 * - Check access on Community
 * - Check access on Conversation
 */
export function setupPageAccess() {
  // eslint-disable-next-line complexity
  router.beforeResolve((
    to,
    from,
    next,
  ) => {
    const isWidget = !!window.widget?.id
    // Check if navigation to be done in widget preview(Transient widget)
    if (isWidget && toBeOpenedInTransientWidget(to)) {
      // Profile - Open it in profile widget
      if (to.name === 'me-profile-index' || to.query.profile)
        openProfileWidgetInTransientMode(to)
      return false
    }

    // Check if navigation to be done in new window
    if (isWidget && toBeOpenedInNewWindow(to)) {
      openInNewTab(to)
      return false
    }

    // check page manager access
    if (to.hash && isX3DContentId(to)) {
      return getX3DContentIdPath(to)
        .then((resultRoute) => {
          const name = resultRoute.name === ROUTE_HOME.LANDING
            ? ROUTE_HOME.HOME
            : (resultRoute.name || ROUTE_HOME.HOME)
          return router.replace({ ...resultRoute, name })
        })
    }
    if (to.hash) {
      return checkV2HashURL(to.hash)
        .then(next)
    }

    if (!checkPageManagerAccess(to)) {
      next(new Error('Content not available'))
      return showContentUnavailableError()
    }

    // content page access check
    if (isContentPage(to) && contentChanged(to, from)) {
      return checkContentAccess(to, from)
        .then(({ redirection } = { redirection: false }) => {
          if (redirection) {
            next({
              name: to.name,
              query: to.query,
              params: to.params,
              force: true,
              replace: true,
            } as RouteLocationAsRelative)
          }
          else { next() }
        })

        .catch((error) => {
          const errorStatus = error.response?.status
          // Unavailable content check routine
          function contentDoesntExist() {
            // content does not exist, then show error message
            if ((errorStatus === STATUS_404) && (error.errorcode === SwymErrorCode.NotFound)) {
              showContentUnavailableError()
              // Let the error handlers handle the error. We need to redirect to home sometimes like when we reject the tou
              // or access the deleted content on click of title in exported PDF
              throw error
            }
          }

          // Maybe this content was accessed without container URI (eg: bookmarks)
          // In that case, we have no info about the container, we should display the error
          if (!('thread_id' in from.params))
            return contentDoesntExist()

          // Show the error message if the item is deleted
          return contentDoesntExist()
        })
    }

    next()
  })
}

/**
 * Check page access at first loading
 * - Resolve V2 URL to V3 URL
 * - Check PageManager access
 * - Check access on Content
 * - Check access on Community
 * - Check access on Conversation
 * - Check access on Admin
 * - Resolve publish-form if url contains page=sharefromcatiadev
 *    - Check paramters from url and navigate to publish form with all the parameters present in the url
 * @param route route to check
 * @returns Promise
 */
export function checkFirstPageAccess(route: RouteLocationNormalized) {
  if (route.hash) {
    if (isX3DContentId(route)) {
      return getX3DContentIdPath(route)
        .then((resultRoute) => {
          const name = resultRoute.name === ROUTE_HOME.LANDING
            ? ROUTE_HOME.HOME
            : (resultRoute.name || ROUTE_HOME.HOME)
          return router.replace({ ...resultRoute, name })
        })
        .catch(() => router.replace({ name: ROUTE_HOME.HOME }))
    }

    if (isDashBoardUrl(route)) {
      return import('~/features-generic/platform/utils/notification-listeners')
        .then(({ checkUrlAndNavigate }) => checkUrlAndNavigate(route.hash, router)
          .catch(() => router.replace({ name: ROUTE_HOME.HOME })))
    }
    return checkV2HashURL(route.hash)
      .then(route => router.replace(route))
      .catch((e) => {
        if (e && !isPrivateCommunityAccessError(e) && e.message !== 'Invalid Route')
          showContentUnavailableError()
        return router.replace({ name: ROUTE_HOME.HOME })
      })
  }
  // check page manager access
  else if (!checkPageManagerAccess(route)) {
    showContentUnavailableError()
    return router.replace({ name: ROUTE_HOME.HOME })
  }
  // content page access check
  else if (isContentPage(route)) {
    return checkContentAccess(route)
      .then(({ redirection } = { redirection: false }) => {
        if (redirection) {
          return router.replace({
            name: route.name,
            query: route.query,
            params: route.params,
            force: true,
          } as RouteLocationAsRelative)
        }
      })
      // content doesn't exist / not enough permission. Close the content lightbox
      .catch((error) => {
        const errorStatus = error.response?.status
        if ((errorStatus === STATUS_404) && (error.errorcode === SwymErrorCode.NotFound))
          showContentUnavailableError()
        router.replace({
          query: { ...router.currentRoute.value.query, content: undefined, edit: undefined },
        })
      })
  }

  return Promise.resolve()
}
